import { useEffect, useState } from 'react';
import {
  AddOnOrder,
  ContactInfoOrder,
  NavbarMobile,
  PaymentOrder,
  ReviewOrder,
  StartOrder,
  StyleOrder,
  SuccessOrder,
} from 'components/layouts/order';
import { Button } from 'components/base';

import { useNavigate } from 'react-router-dom';
import { Footer, Navbar } from 'components/layouts/home';

import {
  Bs1CircleFill,
  Bs2CircleFill,
  Bs3CircleFill,
  BsCheckCircleFill,
} from 'react-icons/bs';
import { TbCalendarEvent } from 'react-icons/tb';

import Logo from 'assets/icons/logo.png';
import { isMobile } from 'react-device-detect';
import BottomTab from 'components/layouts/order/BottomTab';

export type OrderDataProps = {
  isLandingPage: boolean;
  aditionalPages: number;
  style: number;
  brandIdentity: boolean;
  logoDesign: boolean;
  webHosting: boolean;
  emailHosting: boolean;
  domain: boolean;
  contentManagement: boolean;
  name: string;
  email: string;
  phone: string;
};

const OrderScreen = () => {
  const navigate = useNavigate();

  const [orderState, setOrderState] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [orderData, setOrderData] = useState<OrderDataProps>({
    isLandingPage: true,
    aditionalPages: 0,
    style: 0,
    brandIdentity: false,
    logoDesign: false,
    webHosting: false,
    emailHosting: false,
    domain: false,
    contentManagement: false,
    name: '',
    email: '',
    phone: '',
  });

  const toHomePage = () => {
    navigate('/', { replace: true });
  };

  const handlePrevState = () => {
    if (orderState === 0) {
      navigate('/', { replace: true });
    } else {
      setOrderState(orderState - 1);
    }
  };

  const handleNextState = () => {
    setOrderState(orderState + 1);
  };

  const renderOrderComponent = () => {
    switch (orderState) {
      case 0:
        return <StartOrder orderData={orderData} setOrderData={setOrderData} />;
      case 1:
        return <StyleOrder />;
      case 2:
        return <AddOnOrder orderData={orderData} setOrderData={setOrderData} />;
      case 3:
        return (
          <ContactInfoOrder orderData={orderData} setOrderData={setOrderData} />
        );
      case 4:
        return <ReviewOrder />;
      case 5:
        return <PaymentOrder />;
      case 6:
        return <SuccessOrder />;
      default:
        return <StartOrder orderData={orderData} setOrderData={setOrderData} />;
    }
  };

  useEffect(() => {
    const countTotalprice = () => {
      let price = 2000;

      if (orderData.aditionalPages > 0) {
        price += 200 * orderData.aditionalPages;
      }

      if (orderData.brandIdentity) {
        price += 1000;
      }

      if (orderData.logoDesign) {
        price += 500;
      }

      if (orderData.webHosting) {
        price += 2000;
      }

      if (orderData.emailHosting) {
        price += 600;
      }

      if (orderData.domain) {
        price += 200;
      }

      if (orderData.contentManagement) {
        price += 900;
      }

      setTotalPrice(price);
    };

    countTotalprice();
  }, [orderData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [orderState]);

  return (
    <div className="">
      <Navbar className="hidden lg:flex" />
      {orderState !== 6 && (
        <NavbarMobile
          orderState={orderState}
          handlePrevState={handlePrevState}
        />
      )}

      {orderState === 6 && !isMobile ? (
        <div className="hidden items-center justify-center bg-level-up-desktop bg-contain bg-bottom bg-no-repeat px-[10%] pt-[160px] pb-[100px] lg:flex">
          <div className="flex w-[520px] flex-col rounded-3xl bg-white p-[60px] shadow-order">
            <div className="flex flex-col items-center">
              <div className="flex h-[160px] w-[160px] items-center justify-center rounded-full border-4 border-black">
                <img alt="" src={Logo} className="h-[101px] w-20" />
              </div>

              <p className="mt-10 text-center text-3xl font-semibold text-primary-light-blue">
                Thank you!
              </p>
              <p className="mt-5 text-center text-xl font-semibold text-primary-black">
                Order #7312 has been placed!
              </p>
              <p className="mt-5 text-center text-sm text-primary-grey">
                We’ve sent a link to your email with all the info we need to get
                your order started. Our team will reach out to you shortly.
              </p>

              <div className="mt-[60px] flex w-full items-center justify-between">
                <Button
                  theme="OUTLINE"
                  className="w-[48%] border-primary-light-blue !px-0"
                >
                  <p className="whitespace-nowrap text-base font-semibold text-primary-light-blue">
                    Schedule a call
                  </p>
                </Button>
                <Button
                  theme="PRIMARY"
                  className="w-[48%] !px-0"
                  onClick={toHomePage}
                >
                  <p className="whitespace-nowrap text-base font-semibold">
                    Back to homepage
                  </p>
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="lg:px-[10%] lg:pt-[160px] lg:pb-[100px]">
            {orderState < 4 ? (
              <>
                <p className="hidden text-2xl font-semibold lg:block">
                  Start your order
                </p>
                <div className="mt-10 hidden w-[530px] items-center justify-between lg:flex">
                  <div className="flex items-center">
                    <Bs1CircleFill color="#0075FF" className="text-2xl" />
                    <p className="ml-3 text-xl text-primary-black">Website</p>
                  </div>

                  <div className="w-12 border border-tertiary-light-blue" />

                  <div className="flex items-center">
                    <Bs2CircleFill
                      color={orderState >= 2 ? '#0075FF' : '#AAB4C8'}
                      className="text-2xl"
                    />
                    <p
                      className={`ml-3 text-xl ${
                        orderState >= 2
                          ? 'text-tertiary-black'
                          : 'text-tertiary-grey'
                      }`}
                    >
                      Add ons
                    </p>
                  </div>

                  <div className="w-12 border border-tertiary-light-blue" />

                  <div className="flex items-center">
                    <Bs3CircleFill color="#AAB4C8" className="text-2xl" />
                    <p
                      className={`ml-3 text-xl ${
                        orderState === 5
                          ? 'text-primary-black'
                          : 'text-tertiary-grey'
                      }`}
                    >
                      Contact Info
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="hidden justify-between lg:flex">
                <p className="text-[24px] font-semibold text-primary-black">
                  {orderState === 4 ? 'Review' : 'Pay'}
                </p>
                <div className="ml-4 flex w-[420px] items-center justify-between">
                  <div className="flex items-center">
                    <BsCheckCircleFill color="#000" size={22} />
                    <p className="ml-3 text-xl text-primary-black">Order</p>
                  </div>

                  <div className="w-4 border border-tertiary-light-blue lg:w-12" />

                  <div className="flex items-center">
                    {orderState >= 5 ? (
                      <BsCheckCircleFill color="#000" size={22} />
                    ) : (
                      <Bs2CircleFill color="#000" size={22} />
                    )}
                    <p className="ml-3 text-xl text-primary-black">Review</p>
                  </div>

                  <div className="w-4 border border-tertiary-light-blue lg:w-12" />

                  <div className="flex items-center">
                    <Bs3CircleFill
                      color={orderState === 5 ? '#000' : '#AAB4C8'}
                      size={22}
                    />
                    <p
                      className={`ml-3 text-xl ${
                        orderState === 5
                          ? 'text-primary-black'
                          : 'text-tertiary-grey'
                      }`}
                    >
                      Pay
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="items-start justify-between lg:flex">
              <div className="flex-col items-end lg:flex lg:w-full">
                {renderOrderComponent()}

                <div className="mt-8 hidden lg:mr-[5%] lg:flex">
                  <Button
                    theme="OUTLINE"
                    className="w-[164px] border-primary-light-blue"
                    onClick={handlePrevState}
                  >
                    <p className="text-base font-semibold text-primary-light-blue">
                      Back
                    </p>
                  </Button>
                  <Button
                    theme="PRIMARY"
                    className="ml-4 w-[164px]"
                    onClick={handleNextState}
                  >
                    <p className="text-base font-semibold text-white">Next</p>
                  </Button>
                </div>
              </div>

              <div className="mt-10 hidden w-full max-w-[362px] lg:block">
                <div className="flex flex-col rounded-2xl p-6 shadow-order">
                  <p className="text-base font-semibold text-primary-black">
                    Order Summary
                  </p>

                  <div className="my-6 w-full border-b border-[#E4EBF5]" />

                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-primary-black">
                      Landing Page
                    </p>
                    <p className="text-base text-primary-black">₣2000</p>
                  </div>

                  <div className="my-6 w-full border-b border-[#E4EBF5]" />

                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-primary-blue">
                      Basic Plan discount
                    </p>
                    <p className="text-base text-primary-black">₣200</p>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-base font-bold text-primary-blue">
                      Total amount
                    </p>
                    <p className="text-base font-bold text-primary-black">
                      ₣1800
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex items-center rounded-xl bg-background-light-blue py-[22px] px-6">
                  <TbCalendarEvent className="h-6 w-6 text-2xl" />
                  <p className="ml-4 text-base text-primary-black">
                    Est. delivery 7 days
                  </p>
                </div>
              </div>
            </div>
          </div>

          {orderState !== 6 && (
            <BottomTab
              totalPrice={totalPrice}
              handleNextState={handleNextState}
              handlePrevState={handlePrevState}
              orderState={orderState}
              />
          )}
        </>
      )}

      <Footer className="hidden lg:block" />
    </div>
  );
};

export default OrderScreen;
